/**
 * @agent and /skill, painted in the pill's rich editor. The task box mirrors a
 * transparent textarea with an overlay; the rich editor holds REAL text nodes,
 * so the CSS Custom Highlight API paints ranges over them and nothing is
 * written into the editor: its serializer, its caret and its undo never see a
 * span. Where the API is absent (jsdom, an older browser) this is a no-op and
 * the words simply read plain. The same word-boundary rule as the box's
 * generateHighlightedContent: `(^|\s)@slug(?=\s|$)`.
 *
 * The two ::highlight() rules live HERE and are installed at runtime, not in a
 * stylesheet: the build's CSS pipeline rejects the pseudo-element ("'highlight'
 * is not recognized as a valid pseudo-element"), and one leaf's import of such
 * a sheet reaches every route through the pill, so the whole app fails to
 * build. Found 2026-09-20 by running the app; jsdom tests never load CSS.
 * Installed only where the API exists, so the browser's own parser reads them
 * and the bundler never sees them. They carry the task box's own highlight
 * colours (components/message-input/HighlightOverlay.css), in the only
 * properties ::highlight() takes, including its two mention tints: 8% of the
 * ink, 12% under `.dark`, the class ThemeContext puts on the root.
 *
 * The two registry names are page-GLOBAL, so a second mounted leaf would
 * overwrite and then clear the first one's paint. Today the pill has one mount
 * (Dock.tsx).
 */
export const MENTION_HIGHLIGHT = "composer-mention";
export const SKILL_HIGHLIGHT = "composer-skill";

export const HIGHLIGHT_RULES = `::highlight(${MENTION_HIGHLIGHT}) {
  background-color: color-mix(in srgb, var(--fg-default) 8%, transparent);
}
.dark ::highlight(${MENTION_HIGHLIGHT}) {
  background-color: color-mix(in srgb, var(--fg-default) 12%, transparent);
}
::highlight(${SKILL_HIGHLIGHT}) {
  color: #ad1a72;
}`;

const RULES_MARKER = "data-composer-highlights";
let rulesInstalled = false;

/** Once per document. The module flag catches a repaint; the DOM check catches
 *  a hot reload, which hands the page a fresh module beside the old <style>. */
function installHighlightRules(): void {
  if (rulesInstalled || typeof document === "undefined") return;
  rulesInstalled = true;
  if (document.querySelector(`style[${RULES_MARKER}]`)) return;
  const style = document.createElement("style");
  style.setAttribute(RULES_MARKER, "");
  style.textContent = HIGHLIGHT_RULES;
  document.head.appendChild(style);
}

type HighlightCtor = new (...ranges: Range[]) => unknown;
type Registry = {
  set(name: string, value: unknown): void;
  delete(name: string): void;
};

function api(): { Highlight: HighlightCtor; registry: Registry } | null {
  const g = globalThis as {
    Highlight?: HighlightCtor;
    CSS?: { highlights?: Registry };
  };
  if (typeof g.Highlight !== "function" || !g.CSS?.highlights) return null;
  return { Highlight: g.Highlight, registry: g.CSS.highlights };
}

const escapeRegExp = (s: string): string =>
  s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

function rangesFor(
  root: HTMLElement,
  trigger: string,
  slugs: string[]
): Range[] {
  if (slugs.length === 0) return [];
  const pattern = new RegExp(
    `(^|\\s)(${escapeRegExp(trigger)}(?:${slugs.map(escapeRegExp).join("|")}))(?=\\s|$)`,
    "gi"
  );
  const out: Range[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const value = node.textContent ?? "";
    pattern.lastIndex = 0;
    for (let m = pattern.exec(value); m; m = pattern.exec(value)) {
      const start = m.index + m[1].length;
      const range = document.createRange();
      range.setStart(node, start);
      range.setEnd(node, start + m[2].length);
      out.push(range);
    }
  }
  return out;
}

export function paintEditorHighlights(
  root: HTMLElement | null,
  agentSlugs: string[],
  skillSlugs: string[]
): void {
  const found = api();
  if (!found) return;
  // The editor has gone: the last paint holds live Ranges over detached nodes,
  // which tint nothing and are still held. Drop them rather than leave them.
  if (!root) {
    clearEditorHighlights();
    return;
  }
  installHighlightRules();
  const paint = (name: string, ranges: Range[]) => {
    if (ranges.length === 0) found.registry.delete(name);
    else found.registry.set(name, new found.Highlight(...ranges));
  };
  paint(MENTION_HIGHLIGHT, rangesFor(root, "@", agentSlugs));
  paint(SKILL_HIGHLIGHT, rangesFor(root, "/", skillSlugs));
}

export function clearEditorHighlights(): void {
  const found = api();
  if (!found) return;
  found.registry.delete(MENTION_HIGHLIGHT);
  found.registry.delete(SKILL_HIGHLIGHT);
}
